import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  Decision,
  ReviewRun,
  SettingsValues,
  Subscriber,
  Suppression,
} from './types'

const BASE = '/api/stock-out-review'
const KEY = ['stock-out-review'] as const

export function exportUrl(runId: string): string {
  return `${BASE}/runs/${runId}/export.xlsx`
}

/** Latest review (24h server cache), or one stored run when `runId` is set. */
export function useReview(runId?: string) {
  return useQuery({
    queryKey: [...KEY, 'review', runId ?? 'latest'],
    queryFn: () =>
      jsonFetch<ReviewRun>(runId ? `${BASE}/runs/${runId}` : `${BASE}/latest`),
    staleTime: 60_000,
  })
}

export function useRuns() {
  return useQuery({
    queryKey: [...KEY, 'runs'],
    queryFn: () => jsonFetch<{ runs: ReviewRun[] }>(`${BASE}/runs`),
  })
}

export function useRunNow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { send_dm: boolean }) =>
      jsonFetch<{ queued: boolean; task_id: string }>(`${BASE}/run`, {
        method: 'POST',
        body: vars,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, 'runs'] }),
  })
}

export function useClearCache() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      jsonFetch<{ cleared: boolean }>(`${BASE}/cache/clear`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useRecordDecision() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      product_id: number
      decision: Decision
      owner?: string | null
      notes?: string | null
      run_id?: string | null
    }) => jsonFetch<unknown>(`${BASE}/decisions`, { method: 'POST', body: vars }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, 'decisions'] }),
  })
}

export function useDecisions() {
  return useQuery({
    queryKey: [...KEY, 'decisions'],
    queryFn: () =>
      jsonFetch<{
        decisions: {
          product_id: number
          decision: Decision
          owner: string | null
          notes: string | null
          decided_at: string | null
          fingerprint: string | null
        }[]
      }>(`${BASE}/decisions`),
  })
}

export function useSuppressions() {
  return useQuery({
    queryKey: [...KEY, 'suppressions'],
    queryFn: () => jsonFetch<{ suppressions: Suppression[] }>(`${BASE}/suppressions`),
  })
}

export function useAddSuppression() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: Partial<Suppression> & { reason: string }) =>
      jsonFetch<Suppression>(`${BASE}/suppressions`, { method: 'POST', body: vars }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, 'suppressions'] }),
  })
}

export function useToggleSuppression() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; active: boolean }) =>
      jsonFetch<Suppression>(`${BASE}/suppressions/${vars.id}`, {
        method: 'PATCH',
        body: { active: vars.active },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, 'suppressions'] }),
  })
}

export function useReviewSettings() {
  return useQuery({
    queryKey: [...KEY, 'settings'],
    queryFn: () =>
      jsonFetch<{ values: SettingsValues; defaults: SettingsValues }>(`${BASE}/settings`),
  })
}

export function useSaveSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (values: SettingsValues) =>
      jsonFetch<{ values: SettingsValues }>(`${BASE}/settings`, {
        method: 'PUT',
        body: values,
      }),
    onSuccess: (data) => qc.setQueryData([...KEY, 'settings'], (old: unknown) => ({
      ...(old as object),
      values: data.values,
    })),
  })
}

export function useSubscribers(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'subscribers'],
    queryFn: () => jsonFetch<{ subscribers: Subscriber[] }>(`${BASE}/subscribers`),
    enabled,
  })
}

export function useAddSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (email: string) =>
      jsonFetch<Subscriber>(`${BASE}/subscribers`, { method: 'POST', body: { email } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, 'subscribers'] }),
  })
}

export function useDeleteSubscriber() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<unknown>(`${BASE}/subscribers/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, 'subscribers'] }),
  })
}
